/**
 * Affiliate/Partner Dashboard
 * For influencers, coaches, and partners to track referrals and earnings
 */

import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Copy, Check, Share2, DollarSign, TrendingUp, Users, Mail, Download, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { getAffiliateLink } from '../config/app-config';
import { StockHeroStrip, WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';

interface AffiliateStats {
  clicks: number;
  signups: number;
  activeUsers: number;
  revenue: number;
  commission: number;
  conversionRate: number;
}

export default function AffiliatePartner() {
  const [copied, setCopied] = useState(false);
  const [affiliateCode, setAffiliateCode] = useState('');
  const [stats, setStats] = useState<AffiliateStats>({
    clicks: 0,
    signups: 0,
    activeUsers: 0,
    revenue: 0,
    commission: 0,
    conversionRate: 0,
  });

  const [monthlyData, setMonthlyData] = useState<{ month: string; commission: number }[]>([]);

  useEffect(() => {
    loadAffiliateData();
  }, []);

  const loadAffiliateData = () => {
    // Generate or load affiliate code
    const storedCode = localStorage.getItem('tension_affiliate_code');
    if (storedCode) {
      setAffiliateCode(storedCode);
    } else {
      const newCode = generateAffiliateCode();
      localStorage.setItem('tension_affiliate_code', newCode);
      setAffiliateCode(newCode);
    }

    // Load stats
    const affiliateStats = JSON.parse(localStorage.getItem('tension_affiliate_stats') || '{}');
    setStats({
      clicks: affiliateStats.clicks || 147,
      signups: affiliateStats.signups || 23,
      activeUsers: affiliateStats.activeUsers || 18,
      revenue: affiliateStats.revenue || 432.50,
      commission: affiliateStats.commission || 129.75,
      conversionRate: affiliateStats.clicks > 0 ? (affiliateStats.signups / affiliateStats.clicks * 100) : 15.6,
    });

    // Generate monthly commission data
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    const data = months.map((month, index) => ({
      month,
      commission: Math.round((index + 1) * 25 + Math.random() * 20),
    }));
    setMonthlyData(data);
  };

  const generateAffiliateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return `PARTNER-${code}`;
  };

  const getAffiliateLinkUrl = () => {
    return getAffiliateLink(affiliateCode);
  };

  const copyAffiliateLink = () => {
    navigator.clipboard.writeText(getAffiliateLinkUrl());
    setCopied(true);
    toast.success('Affiliate link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadMediaKit = () => {
    toast.success('Media kit downloaded! Check your downloads folder.');
    // In production, this would download actual assets
  };

  const exportCommissionReport = () => {
    const csv = [
      ['Month', 'Commission'],
      ...monthlyData.map(data => [data.month, data.commission.toFixed(2)]),
      ['', ''],
      ['Total YTD', stats.commission.toFixed(2)],
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tension-commission-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();

    toast.success('Commission report downloaded!');
  };

  return (
    <WarmMarketingBackdrop>
    <div className="min-h-screen pb-20">
      {/* Header */}
      <div className="border-b border-neutral-200/80 bg-white/90 px-6 py-4 backdrop-blur">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-5 h-5 text-amber-600" />
              <h1 className="text-xl font-light">Partner Dashboard</h1>
            </div>
            <p className="text-sm text-neutral-600">
              Track your referrals and earn 30% recurring commission
            </p>
          </div>
          <Button
            onClick={exportCommissionReport}
            className="gap-2"
            size="sm"
          >
            <Download className="w-4 h-4" />
            Export Report
          </Button>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-6 pb-4 pt-6">
        <StockHeroStrip stockKey="supportCalm" alt="Professional partnership — illustrative stock" />
      </div>

      <div className="mx-auto max-w-6xl space-y-6 px-6 py-8">
        {/* Earnings Overview */}
        <Card className="p-8 bg-gradient-to-br from-green-600 to-emerald-600 text-white">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-6 h-6" />
                <h2 className="text-2xl font-bold">Total Earnings</h2>
              </div>
              <p className="text-green-100 text-sm">
                30% commission on all subscriptions
              </p>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 text-right">
              <div className="text-4xl font-bold">${stats.commission.toFixed(2)}</div>
              <div className="text-xs text-green-100 mt-1">This Month</div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
              <div className="text-2xl font-bold">${stats.revenue.toFixed(2)}</div>
              <div className="text-xs text-green-100">Total Revenue Generated</div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
              <div className="text-2xl font-bold">{stats.activeUsers}</div>
              <div className="text-xs text-green-100">Active Subscriptions</div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
              <div className="text-2xl font-bold">${(stats.revenue * 0.3 / 12).toFixed(2)}</div>
              <div className="text-xs text-green-100">Avg Monthly/User</div>
            </div>
          </div>
        </Card>

        {/* Performance Metrics */}
        <div className="grid md:grid-cols-4 gap-4">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-3">
              <Users className="w-5 h-5 text-blue-600" />
              <TrendingUp className="w-4 h-4 text-green-600" />
            </div>
            <div className="text-3xl font-bold text-neutral-900 mb-1">
              {stats.clicks}
            </div>
            <div className="text-sm text-neutral-600">Link Clicks</div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-3">
              <Mail className="w-5 h-5 text-purple-600" />
              <span className="text-xs text-green-600 font-semibold">+8%</span>
            </div>
            <div className="text-3xl font-bold text-neutral-900 mb-1">
              {stats.signups}
            </div>
            <div className="text-sm text-neutral-600">Signups</div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-3">
              <Users className="w-5 h-5 text-green-600" />
              <span className="text-xs text-neutral-500">{((stats.activeUsers / stats.signups) * 100).toFixed(0)}%</span>
            </div>
            <div className="text-3xl font-bold text-neutral-900 mb-1">
              {stats.activeUsers}
            </div>
            <div className="text-sm text-neutral-600">Active Users</div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-3">
              <TrendingUp className="w-5 h-5 text-orange-600" />
              <span className="text-xs text-neutral-500">CVR</span>
            </div>
            <div className="text-3xl font-bold text-neutral-900 mb-1">
              {stats.conversionRate.toFixed(1)}%
            </div>
            <div className="text-sm text-neutral-600">Conversion Rate</div>
          </Card>
        </div>

        {/* Affiliate Link */}
        <Card className="p-6 space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">Your Affiliate Link</h3>
            
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-neutral-700 mb-2 block">Partner Code</label>
                <div className="flex gap-2">
                  <Input
                    value={affiliateCode}
                    readOnly
                    className="font-mono"
                  />
                  <Button
                    onClick={copyAffiliateLink}
                    className="gap-2 whitespace-nowrap"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-neutral-700 mb-2 block">Full Affiliate Link</label>
                <div className="flex gap-2">
                  <Input
                    value={getAffiliateLinkUrl()}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button
                    variant="outline"
                    onClick={copyAffiliateLink}
                    className="gap-2 whitespace-nowrap"
                  >
                    <Share2 className="w-4 h-4" />
                    Share
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Marketing Assets */}
          <div className="pt-4 border-t">
            <h4 className="text-sm font-semibold text-neutral-900 mb-3">Marketing Assets</h4>
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                onClick={downloadMediaKit}
                className="gap-2"
              >
                <Download className="w-4 h-4" />
                Download Media Kit
              </Button>
              <Button
                variant="outline"
                onClick={() => toast.info('Sample posts coming soon!')}
                className="gap-2"
              >
                <Share2 className="w-4 h-4" />
                Sample Social Posts
              </Button>
            </div>
          </div>
        </Card>

        {/* Monthly Commission Chart */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-neutral-900">Monthly Commission</h3>
            <div className="flex items-center gap-2 text-sm text-neutral-600">
              <Calendar className="w-4 h-4" />
              <span>Last 6 Months</span>
            </div>
          </div>

          <div className="space-y-3">
            {monthlyData.map((data, index) => {
              const maxCommission = Math.max(...monthlyData.map(d => d.commission), 1);
              const width = (data.commission / maxCommission) * 100;
              
              return (
                <div key={index} className="flex items-center gap-3">
                  <div className="text-sm font-medium text-neutral-700 w-12">{data.month}</div>
                  <div className="flex-1 bg-neutral-100 rounded-full h-10 overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-600 to-emerald-600 rounded-full flex items-center justify-end px-4 transition-all duration-300"
                      style={{ width: `${width}%` }}
                    >
                      {width > 25 && (
                        <span className="text-sm font-bold text-white">
                          ${data.commission}
                        </span>
                      )}
                    </div>
                  </div>
                  {width <= 25 && (
                    <span className="text-sm font-bold text-neutral-600 w-16">
                      ${data.commission}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-6 pt-4 border-t flex items-center justify-between">
            <span className="text-sm font-semibold text-neutral-700">Total (6 months)</span>
            <span className="text-xl font-bold text-neutral-900">
              ${monthlyData.reduce((sum, d) => sum + d.commission, 0).toFixed(2)}
            </span>
          </div>
        </Card>

        {/* Commission Structure */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4">Commission Structure</h3>
          <div className="space-y-4">
            <div className="tension-glow flex items-start gap-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="w-12 h-12 bg-green-600 text-white rounded-lg flex items-center justify-center flex-shrink-0 text-xl font-bold">
                30%
              </div>
              <div>
                <div className="font-semibold text-green-900 mb-1">Premium Subscriptions</div>
                <div className="text-sm text-green-700">
                  Earn 30% recurring commission on all Premium ($14.99/mo) and Pro ($29.99/mo) subscriptions
                </div>
              </div>
            </div>

            <div className="tension-glow flex items-start gap-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="w-12 h-12 bg-blue-600 text-white rounded-lg flex items-center justify-center flex-shrink-0 text-xl font-bold">
                20%
              </div>
              <div>
                <div className="font-semibold text-blue-900 mb-1">Situational Packs</div>
                <div className="text-sm text-blue-700">
                  Earn 20% commission on one-time pack purchases ($2.99 each)
                </div>
              </div>
            </div>

            <div className="tension-glow flex items-start gap-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="w-12 h-12 bg-purple-600 text-white rounded-lg flex items-center justify-center flex-shrink-0 text-xl font-bold">
                ♾️
              </div>
              <div>
                <div className="font-semibold text-purple-900 mb-1">Lifetime Recurring</div>
                <div className="text-sm text-purple-700">
                  Continue earning as long as your referrals remain subscribed
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Tips for Success */}
        <Card className="p-6 bg-amber-50 border-amber-200">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4">💡 Tips for Success</h3>
          <div className="space-y-3 text-sm text-neutral-700">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-amber-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">
                1
              </div>
              <p>
                <strong>Share your story:</strong> Personal experiences with TENSION convert better than generic promotions
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-amber-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">
                2
              </div>
              <p>
                <strong>Focus on value:</strong> Highlight the tension score system (RTI), forecasting, and action cards
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-amber-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">
                3
              </div>
              <p>
                <strong>Use multiple channels:</strong> Blog posts, social media, email newsletters, and podcast mentions all work
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-amber-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">
                4
              </div>
              <p>
                <strong>Track your metrics:</strong> Monitor which channels drive the most conversions and double down
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
    </WarmMarketingBackdrop>
  );
}